import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Loader2, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';
import LeadDrawer from '../components/LeadDrawer.jsx';
import useQuickSale from '../components/QuickSaleModal.jsx';
import { useLead } from '../hooks/useLead.js';
import { apiPost } from '../lib/api.js';
import ScheduleAppointmentModal from '../components/ScheduleAppointmentModal.jsx';
import useBookEvent from '../hooks/useBookEvent.js';
import LeadDetailHeader from '../components/leadDetail/LeadDetailHeader.jsx';
import LeadDetailLeft from '../components/leadDetail/LeadDetailLeft.jsx';
import LeadDetailCenter from '../components/leadDetail/LeadDetailCenter.jsx';
import LeadDetailRight from '../components/leadDetail/LeadDetailRight.jsx';

const DESIGN_KEY = 'leadDetail:useNewDesign';

// Ringy-style 3-column Lead Detail. The "Use new design" toggle (default ON)
// flips between this rebuilt layout and the legacy full-screen LeadDrawer so
// agents can fall back instantly if anything looks off during a live call.
export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { askSale, saleModal } = useQuickSale();

  const [useNewDesign, setUseNewDesign] = useState(() => {
    try { return localStorage.getItem(DESIGN_KEY) !== 'off'; } catch { return true; }
  });

  const { lead, messages, calls, emailMessages, refetch, error, isLoading } = useLead(id);
  const [apptOpen, setApptOpen] = useState(false);
  const bookEvent = useBookEvent(id, () => { setApptOpen(false); refetch(); });

  function goBack() {
    // history.back keeps the Leads list's filter + scroll state intact;
    // fall back to /leads when arriving via direct URL.
    if (window.history.length > 1) navigate(-1);
    else navigate('/leads');
  }

  function toggleDesign(on) {
    setUseNewDesign(on);
    try { localStorage.setItem(DESIGN_KEY, on ? 'on' : 'off'); } catch { /* ignore */ }
  }

  async function markSold() {
    try {
      const sale = await askSale();
      if (sale == null) return;
      await apiPost(`/leads/${id}/disposition`, { status: 'SOLD', ...sale });
      toast.success('Marked as sold');
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to mark sold');
    }
  }

  async function scheduleFollowUp() {
    try {
      await apiPost(`/leads/${id}/disposition`, { status: 'FOLLOW_UP' });
      toast.success('Follow-up scheduled');
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to schedule');
    }
  }

  function createAppointment() {
    if (!lead) return;
    setApptOpen(true);
  }

  // 403 Forbidden: record belongs to an isolated sub-team or parallel team
  if (error?.response?.status === 403 || error?.status === 403) {
    const errorMsg =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      'Access Denied: This record belongs to an isolated sub-team.';

    return (
      <div className="lead-detail-fullscreen -m-6 min-h-[calc(100vh-3rem)] flex items-center justify-center p-6 bg-civix-bg">
        <div className="panel max-w-lg w-full p-8 text-center space-y-5 border border-rose-500/30 bg-[#0d0714] shadow-2xl shadow-rose-950/30 rounded-2xl">
          <div className="w-16 h-16 mx-auto rounded-full bg-rose-500/10 border border-rose-500/30 flex items-center justify-center text-rose-400">
            <ShieldAlert className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <div className="text-[11px] font-mono uppercase tracking-widest text-rose-400 font-semibold">
              403 Forbidden · Sub-Team Privacy Lock
            </div>
            <h2 className="text-xl font-bold text-white tracking-tight">
              {errorMsg}
            </h2>
            <p className="text-white/60 text-sm leading-relaxed">
              CIVIX CRM enforces strict downline privacy and sub-team data isolation. Individual lead pipelines and records are restricted to the direct manager and Super Admin to prevent upline lead theft and data leakage across large agencies.
            </p>
          </div>
          <div className="pt-3">
            <button
              onClick={goBack}
              className="btn-primary inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 px-5 py-2.5 rounded-lg cursor-pointer transition"
            >
              <ArrowLeft className="w-4 h-4" />
              Return to Leads Pipeline
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading && !lead) {
    return (
      <div className="lead-detail-fullscreen -m-6 min-h-[calc(100vh-3rem)] flex items-center justify-center bg-civix-bg">
        <Loader2 className="w-8 h-8 animate-spin text-civix-purple" />
      </div>
    );
  }

  // Missing lead (404 / bad id) or server error with nothing cached to show.
  if (!lead) {
    const status = error?.response?.status || error?.status;
    const isNotFound = status === 404 || !error;
    return (
      <div className="lead-detail-fullscreen -m-6 min-h-[calc(100vh-3rem)] flex items-center justify-center p-6 bg-civix-bg">
        <div className="panel max-w-lg w-full p-8 text-center space-y-5 border border-white/10 bg-[#0d0714] rounded-2xl shadow-2xl">
          <div className="w-16 h-16 mx-auto rounded-full bg-rose-500/10 border border-rose-500/30 flex items-center justify-center text-rose-400">
            <AlertTriangle className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-bold text-white tracking-tight">
              {isNotFound ? 'Lead Not Found' : 'Unable to Load Lead'}
            </h2>
            <p className="text-white/60 text-sm leading-relaxed">
              {isNotFound
                ? 'This lead record does not exist or may have been deleted.'
                : (error?.response?.data?.error || 'A server error occurred while retrieving this lead.')}
            </p>
          </div>
          <button
            onClick={() => navigate('/leads')}
            className="btn-primary inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 px-5 py-2.5 rounded-lg transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Return to Leads List
          </button>
        </div>
      </div>
    );
  }

  // Legacy full-screen view (toggle OFF): mount the existing LeadDrawer inline.
  if (!useNewDesign) {
    return (
      <div className="lead-detail-fullscreen -m-6 min-h-[calc(100vh-3rem)] flex flex-col bg-civix-bg">
      {saleModal}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-civix-border bg-civix-panel">
          <button
            onClick={goBack}
            className="btn-ghost text-xs flex items-center gap-1.5"
            aria-label="Back to Leads"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Leads
          </button>
          <div className="text-xs text-white/40">
            <button onClick={goBack} className="hover:text-white/70 transition">Leads</button>
            <span className="mx-1">/</span>
            <span className="text-white/70">Detail</span>
          </div>
          <label className="ml-auto flex items-center gap-1.5 text-[11px] text-white/60 cursor-pointer select-none">
            <input type="checkbox" checked={useNewDesign} onChange={(e) => toggleDesign(e.target.checked)} className="accent-civix-purple" />
            Use new design
          </label>
        </div>
        <div className="flex-1 overflow-y-auto">
          <LeadDrawer
            leadId={id}
            inline
            onClose={goBack}
            onChange={refetch}
          />
        </div>
      </div>
    );
  }

  // New 3-column design (default).
  return (
    <div className="lead-detail-fullscreen -m-6 min-h-[calc(100vh-3rem)] flex flex-col bg-civix-bg">
      <LeadDetailHeader
        leadId={id}
        lead={lead}
        useNewDesign={useNewDesign}
        onToggleDesign={toggleDesign}
        onBack={goBack}
        onNavigate={(nid) => navigate(`/leads/${nid}`)}
      />
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)_300px] gap-4 p-4 overflow-hidden">
        <aside className="min-h-0 overflow-hidden">
          <LeadDetailLeft
            lead={lead}
            leadId={id}
            onChange={refetch}
            onScheduleFollowUp={scheduleFollowUp}
            onCreateAppointment={createAppointment}
            onMarkSold={markSold}
          />
        </aside>
        <main className="min-h-0 overflow-hidden">
          <LeadDetailCenter
            leadId={id}
            lead={lead}
            messages={messages}
            calls={calls}
            emailMessages={emailMessages}
            onChange={refetch}
            onCreateAppointment={createAppointment}
          />
        </main>
        <aside className="min-h-0 overflow-y-auto">
          <LeadDetailRight leadId={id} lead={lead} onChange={refetch} />
        </aside>
      </div>
      {apptOpen && lead && <ScheduleAppointmentModal lead={lead} onCancel={() => setApptOpen(false)} onSubmit={bookEvent} />}
    </div>
  );
}
